import { Select, Tag } from "antd"
import { useContext, useEffect, useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { ReactComponent as CALENDAR } from "../../../assets/CalendarICON.svg"
import { ReactComponent as CHECK } from "../../../assets/CheckCircle.svg"
import { ReactComponent as DELETE } from "../../../assets/DeleteICON.svg"
import { ReactComponent as EDIT } from "../../../assets/EditICON.svg"
import { ReactComponent as INFO } from "../../../assets/InfoICON.svg"
import { ReactComponent as PLUSCIRCLE } from "../../../assets/PlusInCircleIcon.svg"
import { ReactComponent as TASKS } from "../../../assets/Task.svg"
import { GeneralQueryKeys } from "../../../api/constants"
import { P } from "../../../auth/permissions"
import { Authorized } from "../../../components/authentication/Authorized"
import { useAbility } from "../../../components/contexts/AbilityContext"
import { ModalContext } from "../../../components/contexts/ModalContext"
import Loading from "../../../components/common/Loading"
import BaseButton from "../../../components/elements/BaseButton"
import BaseInputArea from "../../../components/elements/BaseInputArea"
import BaseInputField from "../../../components/elements/BaseInputField"
import BaseModal from "../../../components/layout/BaseModal"
import TourActiveToggle from "../../Touren/components/TourActiveToggle"
import TourDeletePopover from "../../Touren/components/TourDeletePopover"
import { getServiceCatalog } from "../../Touren/api/apiCalls"
import {
    useCreateServiceCatalogItemMutation,
    useDeleteServiceCatalogItemMutation,
    useGenerateTourTasksMutation,
    useUpdateServiceCatalogItemMutation,
} from "../../Touren/api/services"
import { intervalLabel, intervalSelectValue, SERVICE_INTERVAL_OPTIONS } from "../../Touren/data/serviceIntervals"
import { ServiceCatalogItem } from "../../Touren/types"

type ServiceCatalogModalType = {
    modalId: number
    objectId: string
}

type CatalogDraft = {
    activity: string
    description: string
    intervalValue: string
    delayWeeks: string
    active: boolean
}

const emptyDraft = (): CatalogDraft => ({ activity: "", description: "", intervalValue: "1", delayWeeks: "0", active: true })

const itemWeeks = (item: ServiceCatalogItem) => Math.max(1, Number(item.intervalWeeks) || 1)

const draftFromItem = (item: ServiceCatalogItem): CatalogDraft => ({
    activity: item.activity,
    description: item.description ?? "",
    intervalValue: intervalSelectValue(itemWeeks(item)),
    delayWeeks: String(item.delayWeeks),
    active: item.active,
})

const draftIntervalWeeks = (draft: CatalogDraft) => Math.max(1, Number(draft.intervalValue) || 1)

const delayText = (weeks: number) => {
    if (weeks <= 0) return "Ohne Verzögerung"
    if (weeks === 1) return "1 Woche Verzögerung"
    return `${weeks} Wochen Verzögerung`
}

export default function ServiceCatalogModal(props: ServiceCatalogModalType) {
    const ability = useAbility()
    const modalCon = useContext(ModalContext)
    const canEdit = ability.can(P.termine_management.action, P.termine_management.subject)
    const queryClient = useQueryClient()
    const modalOpen = modalCon?.open === true && modalCon?.modalId === props.modalId
    const { data, isLoading } = useQuery({
        queryKey: [GeneralQueryKeys.SERVICE_CATALOG + props.objectId],
        queryFn: () => getServiceCatalog(props.objectId),
        enabled: !!props.objectId && modalOpen,
    })
    const { mutate: createItem } = useCreateServiceCatalogItemMutation(queryClient)
    const { mutate: updateItem } = useUpdateServiceCatalogItemMutation(queryClient)
    const { mutate: deleteItem } = useDeleteServiceCatalogItemMutation(queryClient)
    const { mutate: generateTasks, isPending: generating } = useGenerateTourTasksMutation(queryClient)

    const [adding, setAdding] = useState(false)
    const [draft, setDraft] = useState<CatalogDraft>(emptyDraft)

    const resetAdd = () => {
        setDraft(emptyDraft())
        setAdding(false)
    }

    const delayNumber = Number(draft.delayWeeks)
    const intervalWeeks = draftIntervalWeeks(draft)
    const canAdd = draft.activity.trim().length > 0 && Number.isInteger(delayNumber) && delayNumber >= 0 && Number.isInteger(intervalWeeks) && intervalWeeks >= 1

    const addItem = () => {
        if (!canAdd) return
        createItem(
            {
                objectId: props.objectId,
                activity: draft.activity.trim(),
                description: draft.description.trim(),
                intervalWeeks,
                delayWeeks: delayNumber,
                active: draft.active,
            },
            { onSuccess: resetAdd },
        )
    }

    return (
        <BaseModal
            modalId={props.modalId}
            title="Leistungsverzeichnis"
            action={
                canEdit ? (
                    <PLUSCIRCLE
                        className={`w-8 h-8 cursor-pointer hover:text-primary ${adding ? "text-primary" : ""}`}
                        title="Neue Leistung"
                        onClick={() => setAdding((open) => !open)}
                    />
                ) : undefined
            }
            onOpen={resetAdd}
            bottomRow={
                canEdit ? (
                    <div className="flex justify-end w-full">
                        <button className="btn btn-outline" type="button" disabled={generating || (data ?? []).length === 0} onClick={() => generateTasks(props.objectId)}>
                            Vorlauf ergänzen
                        </button>
                    </div>
                ) : undefined
            }>
            {canEdit && adding && (
                <Authorized allOf={[P.termine_management]}>
                    <div className="flex flex-col gap-4 px-2 pt-4 pb-4">
                        <p className="text-sm font-semibold">Neue Leistung</p>
                        <CatalogFormFields draft={draft} onChange={setDraft} />
                        <div className="flex justify-end gap-2">
                            <BaseButton label="Abbrechen" func={resetAdd} variant="ghost" />
                            <BaseButton label="Hinzufügen" func={addItem} colorway="secondary" disabled={!canAdd} />
                        </div>
                    </div>
                </Authorized>
            )}
            {isLoading && <Loading />}
            {!isLoading && (data ?? []).length === 0 && <p className="text-center text-gray-300 font-bold text-sm uppercase py-6">Keine Leistungen</p>}
            <div className="flex flex-col gap-3 px-2 pb-4">
                {(data ?? []).map((item) => (
                    <CatalogItemCard key={item._id} item={item} canEdit={canEdit} onUpdate={updateItem} onDelete={() => deleteItem(item._id)} />
                ))}
            </div>
        </BaseModal>
    )
}

type CatalogFormFieldsType = {
    draft: CatalogDraft
    onChange: (draft: CatalogDraft) => void
}

function CatalogFormFields(props: CatalogFormFieldsType) {
    return (
        <div className="flex flex-col gap-4">
            <div className="flex gap-3 items-start">
                <TASKS className="w-6 h-6 shrink-0 mt-8 text-primary" />
                <BaseInputField
                    label="Leistung *"
                    type="text"
                    value={props.draft.activity}
                    onChange={(activity) => props.onChange({ ...props.draft, activity })}
                    placeholder="z. B. Außenanlagenpflege"
                />
            </div>
            <div className="flex gap-3 items-start">
                <INFO className="w-6 h-6 shrink-0 mt-8 text-primary" />
                <BaseInputArea
                    label="Aufgabenbeschreibung"
                    value={props.draft.description}
                    onChange={(description) => props.onChange({ ...props.draft, description })}
                    placeholder="Was am Objekt zu tun ist. Wird in der Aufgabenübersicht angezeigt."
                    minRows={2}
                    maxRows={6}
                />
            </div>
            <div className="flex gap-3 items-start">
                <CALENDAR className="w-6 h-6 shrink-0 mt-8 text-primary" />
                <div className="flex flex-col w-full z-10">
                    <label className="text-sm pl-2 pb-1">Intervall *</label>
                    <Select
                        variant="borderless"
                        style={{ height: "3.5rem", backgroundColor: "white" }}
                        className="w-full rounded-default shadow-lg"
                        value={props.draft.intervalValue}
                        options={SERVICE_INTERVAL_OPTIONS.map((option) => ({ value: String(option.weeks), label: option.label }))}
                        onChange={(intervalValue) => props.onChange({ ...props.draft, intervalValue })}
                    />
                </div>
            </div>
            <div className="flex gap-3 items-start">
                <INFO className="w-6 h-6 shrink-0 mt-8 text-primary" />
                <BaseInputField
                    label="Verzögerung (Wochen)"
                    type="number"
                    min="0"
                    value={props.draft.delayWeeks}
                    onChange={(delayWeeks) => props.onChange({ ...props.draft, delayWeeks })}
                    explainText="Startpunkt in Wochen, damit nicht alle Aufgaben eines Intervalls gleichzeitig anfallen."
                />
            </div>
            <div className="flex gap-3 items-start">
                <CHECK className="w-6 h-6 shrink-0 mt-8 text-primary" />
                <TourActiveToggle active={props.draft.active} onChange={(active) => props.onChange({ ...props.draft, active })} />
            </div>
        </div>
    )
}

type CatalogItemCardType = {
    item: ServiceCatalogItem
    canEdit: boolean
    onUpdate: (data: { id: string; activity?: string; description?: string; intervalWeeks?: number; delayWeeks?: number; active?: boolean }) => void
    onDelete: () => void
}

function CatalogItemCard(props: CatalogItemCardType) {
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState<CatalogDraft>(emptyDraft())

    const startEdit = () => {
        setDraft(draftFromItem(props.item))
        setEditing(true)
    }

    useEffect(() => {
        if (!editing) return
        setDraft(draftFromItem(props.item))
    }, [editing, props.item])

    const delayNumber = Number(draft.delayWeeks)
    const intervalWeeks = draftIntervalWeeks(draft)
    const canSave =
        draft.activity.trim().length > 0 &&
        Number.isInteger(delayNumber) &&
        delayNumber >= 0 &&
        Number.isInteger(intervalWeeks) &&
        intervalWeeks >= 1 &&
        (draft.activity.trim() !== props.item.activity ||
            draft.description.trim() !== (props.item.description ?? "") ||
            intervalWeeks !== itemWeeks(props.item) ||
            delayNumber !== props.item.delayWeeks ||
            draft.active !== props.item.active)

    const save = () => {
        if (!canSave) return
        props.onUpdate({
            id: props.item._id,
            activity: draft.activity.trim(),
            description: draft.description.trim(),
            intervalWeeks,
            delayWeeks: delayNumber,
            active: draft.active,
        })
        setEditing(false)
    }

    return (
        <div className={`bg-white rounded-default shadow-lg p-3 flex flex-col gap-2 ${!props.item.active ? "text-gray-400" : ""}`}>
            <div className="flex justify-between gap-2 items-start">
                <div className="min-w-0">
                    <p className="font-semibold text-sm truncate" title={props.item.activity}>
                        {props.item.activity}
                    </p>
                    {props.item.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{props.item.description}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Tag className="m-0" color={props.item.active ? "green" : "default"}>
                        {props.item.active ? "Aktiv" : "Pausiert"}
                    </Tag>
                    {props.canEdit && (
                        <>
                            <EDIT
                                className={`w-5 h-5 shrink-0 cursor-pointer hover:text-primary ${editing ? "text-primary" : ""}`}
                                title="Bearbeiten"
                                onClick={() => (editing ? setEditing(false) : startEdit())}
                            />
                            <TourDeletePopover text="Wollen Sie diese Leistung wirklich entfernen?" onConfirm={props.onDelete}>
                                <DELETE className="w-5 h-5 shrink-0 cursor-pointer hover:text-error" />
                            </TourDeletePopover>
                        </>
                    )}
                </div>
            </div>
            {!editing && (
                <div className="flex flex-col gap-2 px-0.5">
                    <div className="flex items-center min-w-0">
                        <CALENDAR className="w-5 h-5 shrink-0 text-primary" />
                        <p className="pl-2 text-sm line-clamp-1">{intervalLabel(itemWeeks(props.item))}</p>
                    </div>
                    <div className="flex items-center min-w-0">
                        <INFO className="w-5 h-5 shrink-0 text-primary" />
                        <p className="pl-2 text-sm line-clamp-1">{delayText(props.item.delayWeeks)}</p>
                    </div>
                </div>
            )}
            {editing && (
                <div className="flex flex-col gap-4 pt-2 border-t">
                    <CatalogFormFields draft={draft} onChange={setDraft} />
                    <div className="flex justify-end gap-2">
                        <BaseButton label="Abbrechen" func={() => setEditing(false)} variant="ghost" size="small" />
                        <BaseButton label="Speichern" func={save} colorway="secondary" size="small" disabled={!canSave} />
                    </div>
                </div>
            )}
        </div>
    )
}
